import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Trash2, Plus } from "lucide-react";
import { refreshBranding, useBranding } from "@/lib/branding";
import { MASCOT_URL } from "@/components/Mascot";
import { BACKEND_URL } from "@/lib/api";

const BTN = "px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none";
const COLOR_FIELDS = [["primary", "Buttons & links"], ["success", "Correct / Play"], ["accent", "Highlights"], ["bg", "Page background"]];
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function ThemeCard({ t, active, onActivate, onDelete, onUpload, baseMascot }) {
  const mascot = abs(t.mascot_url) || baseMascot || MASCOT_URL;
  return (
    <div className="sticker-card overflow-hidden" data-testid={`theme-card-${t.id}`} style={{ background: t.colors.bg }}>
      <div className="p-5 flex items-center gap-4">
        <img src={mascot} alt={t.name} className="w-20 h-20 rounded-full border-[3px] border-[#374151] object-cover bg-white" />
        <div className="flex-1 min-w-0">
          <p className="font-black text-lg truncate">{t.name}</p>
          <div className="flex gap-1 mt-2">
            {COLOR_FIELDS.map(([k]) => (
              <span key={k} title={k} className="w-6 h-6 rounded-full border-[2px] border-[#374151]" style={{ background: t.colors[k] }} />
            ))}
          </div>
        </div>
      </div>
      <div className="px-5 pb-5 flex gap-2 flex-wrap items-center">
        {active ? (
          <span className="px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-white font-black text-sm inline-flex items-center gap-1" data-testid={`theme-active-${t.id}`}>
            <Check size={14} strokeWidth={3} /> Active
          </span>
        ) : (
          <button onClick={onActivate} data-testid={`activate-theme-${t.id}`} className={BTN} style={{ background: t.colors.primary, color: "#fff" }}>
            Use this theme
          </button>
        )}
        {!t.preset && (<>
          <label className={`${BTN} bg-white cursor-pointer`}>
            Mascot
            <input type="file" accept="image/*" className="hidden" data-testid={`theme-mascot-${t.id}`} onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
          <button onClick={onDelete} className="p-2 text-[#FF4B4B]" data-testid={`delete-theme-${t.id}`}><Trash2 size={18} /></button>
        </>)}
      </div>
    </div>
  );
}

export default function AdminThemes({ api }) {
  const [data, setData] = useState({ active_id: "default", themes: [] });
  const [form, setForm] = useState({ name: "", colors: { primary: "#1CB0F6", success: "#58CC02", accent: "#FFC800", bg: "#F7F9FA" } });
  const { base_mascot_url } = useBranding();
  const load = useCallback(() => api.get("/admin/themes").then((r) => setData(r.data)), [api]);
  useEffect(() => { load(); }, [load]);

  const activate = async (id) => { await api.post(`/admin/themes/${id}/activate`); await refreshBranding(); load(); toast.success("Theme applied for everyone"); };
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name your theme");
    try { await api.post("/admin/themes", form); toast.success("Theme created"); setForm({ ...form, name: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete theme?")) return; await api.delete(`/admin/themes/${id}`); await refreshBranding(); load(); };
  const upload = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/themes/${id}/mascot`, fd); await refreshBranding(); load(); toast.success("Mascot updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div>
      <p className="text-slate-600 mb-4 font-bold">One click changes the colors and the mascot for everyone.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="themes-grid">
        {data.themes.map((t) => (
          <ThemeCard key={t.id} t={t} active={data.active_id === t.id} baseMascot={t.id === "default" ? base_mascot_url : ""}
            onActivate={() => activate(t.id)} onDelete={() => del(t.id)} onUpload={(f) => upload(t.id, f)} />
        ))}
      </div>

      <div className="sticker-card p-6 mt-8" style={{ background: form.colors.bg }}>
        <h3 className="font-black text-lg mb-3">Make your own theme</h3>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. St. Patrick's Day" data-testid="custom-theme-name"
          className="w-full sm:w-80 px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold bg-white" />
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {COLOR_FIELDS.map(([k, label]) => (
            <label key={k} className="font-bold text-sm flex items-center gap-2">
              <input type="color" value={form.colors[k]} data-testid={`custom-theme-${k}`}
                onChange={(e) => setForm({ ...form, colors: { ...form.colors, [k]: e.target.value.toUpperCase() } })}
                className="w-10 h-10 rounded-lg border-[2px] border-[#374151] cursor-pointer" />
              {label}
            </label>
          ))}
        </div>
        <div className="mt-4 flex gap-2 items-center flex-wrap">
          <span className={BTN} style={{ background: form.colors.primary, color: "#fff" }}>Preview button</span>
          <span className={BTN} style={{ background: form.colors.success, color: "#fff" }}>Play</span>
          <span className={BTN} style={{ background: form.colors.accent }}>Highlight</span>
        </div>
        <button onClick={create} data-testid="create-custom-theme" className={`${BTN} mt-5 bg-white inline-flex items-center gap-1`}>
          <Plus size={16} strokeWidth={2.5} /> Save theme
        </button>
        <p className="text-sm text-slate-600 mt-2">After saving, upload a mascot on its card (optional — otherwise the default mascot is used).</p>
      </div>
    </div>
  );
}
